import { Paperclip, Plus, Upload } from "lucide-react";
import * as React from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { cn } from "src/common/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";

import { DROPZONE_ACCEPT, MAX_ATTACHMENTS_PER_MESSAGE } from "./attachment-constants";
import { ChatInput } from "./chat-input";
import { ChatAttachmentChips } from "./ChatAttachmentChips";
import { type ChatAttachmentType,useChatAttachments } from "./useChatAttachments";

interface ChatComposerProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onSubmit" | "value"> {
  onSend?: (message: string, attachments: ChatAttachmentType[]) => void;
  sendDisabled?: boolean;
  defaultValue?: string;
  /** Backwards-compat — ChatInput ignores this; kept so callers don't break. */
  value?: string;
  showInterrupt?: boolean;
  onInterrupt?: () => void;
  isInterrupting?: boolean;
  onSubmitWhileDisabled?: (message: string) => void;
  /** Forwarded to <ChatInput>. */
  inputClassName?: string;
  /** Override classes on the attachment "+" button (e.g. when the input is taller and needs more left offset). */
  attachmentButtonClassName?: string;
}

interface ChatComposerRefType extends HTMLTextAreaElement {
  setInputMessage?: (message: string) => void;
}

const PASTE_FILE_PREFIX = "pasted-image";

const generatePastedFilename = (mimetype: string): string => {
  const ext = mimetype.split("/")[1] || "png";
  return `${PASTE_FILE_PREFIX}-${Date.now()}.${ext}`;
};

/**
 * Composer wraps ChatInput with attachment chips, drag-and-drop, click-to-attach,
 * and paste-image support. It owns no message text state; the underlying ChatInput
 * keeps that. It does own attachment state via useChatAttachments.
 */
export const ChatComposer = React.forwardRef<ChatComposerRefType, ChatComposerProps>(
  ({ onSend, sendDisabled, inputClassName, attachmentButtonClassName, ...inputProps }, ref) => {
    const { attachments, isUploading, hasError, addFiles, removeAttachment, clearAll } = useChatAttachments();

    const onDrop = React.useCallback(
      (accepted: File[]) => {
        if (accepted.length > 0) addFiles(accepted);
      },
      [addFiles]
    );

    // Dropzone-rejected files flow into the SAME addFiles entry point as accepted ones
    // (with a preset error), so every file becomes a visible chip — never a silent drop.
    // `maxSize`/`maxFiles` are deliberately NOT set on the dropzone: size and count are
    // owned by addFiles' validation, which produces error chips instead of silently
    // filtering. With `accept` still set, `file-invalid-type` is the only rejection code
    // dropzone can emit; the generic fallback guards against future config changes.
    const onDropRejected = React.useCallback(
      (rejections: FileRejection[]) => {
        if (rejections.length === 0) return;
        const presetErrors = new Map<File, string>();
        for (const rejection of rejections) {
          const isInvalidType = rejection.errors.some((e) => e.code === "file-invalid-type");
          presetErrors.set(rejection.file, isInvalidType ? "Unsupported file type" : "File was rejected");
        }
        addFiles(Array.from(presetErrors.keys()), presetErrors);
      },
      [addFiles]
    );

    const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
      onDrop,
      onDropRejected,
      accept: DROPZONE_ACCEPT,
      noClick: true, // We attach click to a dedicated paperclip button.
      noKeyboard: true,
    });

    const handlePaste = React.useCallback(
      (e: React.ClipboardEvent<HTMLDivElement>) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        const files: File[] = [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === "file" && item.type.startsWith("image/")) {
            const blob = item.getAsFile();
            if (blob) {
              const named = new File([blob], generatePastedFilename(blob.type), { type: blob.type });
              files.push(named);
            }
          }
        }
        if (files.length > 0) {
          e.preventDefault();
          addFiles(files);
        }
      },
      [addFiles]
    );

    const handleSend = React.useCallback(
      (message: string) => {
        if (!onSend) return;
        if (isUploading || hasError) return;
        const ready = attachments.filter((a) => a.status === "ready");
        onSend(message, ready);
        clearAll();
      },
      [onSend, attachments, isUploading, hasError, clearAll]
    );

    const composedSendDisabled = sendDisabled || isUploading || hasError;

    return (
      <div
        {...getRootProps({
          onPaste: handlePaste,
          className: "relative",
        })}
      >
        <input {...getInputProps()} />

        {/* Drop overlay — scoped to composer surface, not full window. */}
        {isDragActive && (
          <div
            aria-hidden="true"
            className="absolute inset-0 z-30 flex items-center justify-center rounded-md border-2 border-dashed border-primary/60 bg-popover/80 pointer-events-none"
          >
            <div className="flex items-center gap-2 text-sm text-foreground">
              <Upload className="h-4 w-4" />
              <span>Drop files to attach</span>
            </div>
          </div>
        )}

        <ChatAttachmentChips attachments={attachments} onRemove={removeAttachment} />

        <div className="relative flex items-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
                aria-label="Open composer menu"
                title={
                  attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE
                    ? `Maximum ${MAX_ATTACHMENTS_PER_MESSAGE} attachments`
                    : undefined
                }
                className={cn(
                  "absolute left-1 top-1/2 -translate-y-1/2 z-10 h-8 w-8 flex items-center justify-center rounded-full transition-colors duration-200",
                  attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE
                    ? "text-muted-foreground/40 cursor-not-allowed"
                    : "text-muted-foreground hover:bg-foreground/10 hover:text-foreground",
                  attachmentButtonClassName
                )}
              >
                <Plus className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="min-w-[180px]">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  openFilePicker();
                }}
                disabled={attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Add attachments
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <ChatInput
            ref={ref}
            {...inputProps}
            sendDisabled={composedSendDisabled}
            onSend={handleSend}
            leftInsetPx={44}
            className={inputClassName}
          />
        </div>
      </div>
    );
  }
);
ChatComposer.displayName = "ChatComposer";

export type { ChatComposerRefType };
